import type { Dispatch, SetStateAction } from "react";
import React from "react";
import type { Card } from "../../../../api/training/Training.typed";
import { Box, Checkbox } from "@mui/joy";
import DarkTypography from "../../../common/DarkTypography";
import { SCreditCard } from "../../../../styles/StyledMUI";
import { colorTokens, withAlpha } from "../../../../styles/globalStyles";

export const CARD_ORG_CHIP_MIN_HEIGHT = 28;

type CardOrganisationStatusProps = {
  index: number;
  cardOrganisation: string;
  cardStatus: boolean;
  cards: Card[];
  setCards: Dispatch<SetStateAction<Card[]>>;
  handleSave: (cardsToSave: Card[]) => void;
};

const chipSlotBaseSx = {
  width: "fit-content",
  maxWidth: "100%",
  minHeight: CARD_ORG_CHIP_MIN_HEIGHT,
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "center",
  gap: 0.5,
  px: 0.75,
  borderRadius: "16px",
  cursor: "pointer",
  border: "1px solid",
  transition: "background-color 0.15s, border-color 0.15s",
} as const;

const organisationCardIconSx = {
  flexShrink: 0,
  fontSize: "0.95rem",
  color: colorTokens.text.title,
  opacity: 0.95,
} as const;

const organisationCheckboxSx = {
  "--Checkbox-size": "14px",
  "--Icon-fontSize": "12px",
  flexShrink: 0,
  "& .MuiCheckbox-checkbox": {
    backgroundColor: colorTokens.bg.surfaceElevated,
    borderColor: colorTokens.border.strong,
  },
  "&:hover .MuiCheckbox-checkbox": {
    borderColor: colorTokens.border.focus,
    backgroundColor: colorTokens.bg.hover,
  },
  "&.Mui-checked .MuiCheckbox-checkbox": {
    backgroundColor: colorTokens.status.success,
    borderColor: colorTokens.status.success,
    color: colorTokens.text.primary,
  },
};

export default function CardOrganisationStatus({
  index,
  cardOrganisation,
  cardStatus,
  cards,
  setCards,
  handleSave,
}: CardOrganisationStatusProps) {
  const [status, setStatus] = React.useState<boolean>(cardStatus);

  React.useEffect(() => {
    setStatus(cardStatus);
  }, [cardStatus]);

  const handleChange = () => {
    const newStatus = !status;
    setStatus(newStatus);
    const updatedCards = cards.map((c, i) => (i === index ? { ...c, Status: newStatus } : c));
    setCards(updatedCards);
    handleSave(updatedCards);
  };

  const label = cardOrganisation.toUpperCase();

  return (
    <Box
      component="label"
      sx={{
        ...chipSlotBaseSx,
        backgroundColor: status
          ? withAlpha(colorTokens.status.success, 0.2)
          : colorTokens.bg.surfaceMuted,
        borderColor: status
          ? withAlpha(colorTokens.status.success, 0.55)
          : colorTokens.border.default,
        "&:hover": {
          backgroundColor: status
            ? withAlpha(colorTokens.status.success, 0.28)
            : withAlpha(colorTokens.text.primary, 0.08),
        },
      }}
    >
      <Checkbox checked={status} onChange={handleChange} size="sm" sx={organisationCheckboxSx} />
      <SCreditCard sx={organisationCardIconSx} aria-hidden />
      <DarkTypography
        level="body-xs"
        colorVariant="primary"
        sx={{
          whiteSpace: "nowrap",
          fontWeight: 600,
        }}
      >
        {label}
      </DarkTypography>
    </Box>
  );
}
